import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { BsArrowLeft, BsSearch } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import Container from "../../components/Container";
import Footer from "../../components/footer";
import FullPageLoader from "../../components/FullPageLoader";
import Header from "../../components/Header";
import { PROTECTED_PATHS } from "../../app/constants";
import { COLORS } from "../../constants/colors";
import { fetchFoodListings } from "./service";
import FoodCard from "./components/FoodCard";

const VendorFoods = () => {
  const pageBg = useColorModeValue(COLORS.bgGrey, COLORS.darkModeBg);
  const cardBg = useColorModeValue(COLORS.white, COLORS.grey);
  const heroBg = useColorModeValue(COLORS.primary, COLORS.black);
  const appTextColor = useColorModeValue(COLORS.primary, COLORS.lightGrey);
  const mutedColor = useColorModeValue("#707070", COLORS.lightGrey);
  const searchBg = useColorModeValue("#EFEFEF", "#1f1f2e");

  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { vendorId } = useParams();
  const navigate = useNavigate();
  const decodedVendorName = decodeURIComponent(vendorId);

  useEffect(() => {
    fetchFoodListings(setLoading, setFoods, setError);
  }, []);

  const vendorFoods = useMemo(
    () =>
      foods.filter(
        (item) =>
          item.vendorName?.toLowerCase() === decodedVendorName.toLowerCase()
      ),
    [foods, decodedVendorName]
  );

  const filteredFoods = useMemo(() => {
    if (!searchTerm.trim()) return vendorFoods;

    return vendorFoods.filter((item) => {
      const search = searchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(search) ||
        item.category?.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search) ||
        item.location?.toLowerCase().includes(search)
      );
    });
  }, [vendorFoods, searchTerm]);

  if (loading) {
    return <FullPageLoader bg={pageBg} />;
  }

  return (
    <Stack pb="15vh" bg={pageBg} minH="100vh">
      <Box shadow="sm">
        <Container>
          <Header menu />
        </Container>
      </Box>

      <Container>
        <Stack spacing="5" pt="5" pb="10">
          <Box bg={heroBg} borderRadius="16px" p="5" color={COLORS.white}>
            <Flex align="center" gap="2">
              <Button
                size="sm"
                bg="rgba(255,255,255,0.14)"
                color={COLORS.white}
                onClick={() => navigate(PROTECTED_PATHS.FOOD_LISTINGS)}
                _hover={{ bg: "rgba(255,255,255,0.22)" }}
                minW="8"
                px="2"
              >
                <BsArrowLeft />
              </Button>
              <Text fontSize="xl" fontWeight="700" noOfLines={1}>
                {decodedVendorName}'s Foods
              </Text>
            </Flex>
            <Text mt="1" fontSize="sm" opacity="0.9">
              Browse all food items listed by {decodedVendorName}.
            </Text>
            <Text mt="3" fontSize="xs" opacity="0.8">
              {vendorFoods.length} item{vendorFoods.length === 1 ? "" : "s"} found
            </Text>
          </Box>

          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <BsSearch color={mutedColor} />
            </InputLeftElement>
            <Input
              placeholder="Search foods by name, category, location..."
              bg={searchBg}
              border="none"
              borderRadius="999px"
              color={appTextColor}
              _placeholder={{ color: mutedColor }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {error ? (
            <Flex bg={cardBg} borderRadius="12px" p="4" justify="center">
              <Text textAlign="center" color={appTextColor}>
                {error}
              </Text>
            </Flex>
          ) : null}

          {!error && filteredFoods.length === 0 ? (
            <Flex bg={cardBg} borderRadius="12px" p="6" justify="center">
              <Text textAlign="center" color={appTextColor}>
                {vendorFoods.length === 0
                  ? "This vendor has no food listings at the moment."
                  : `No food items match your search for "${searchTerm}".`}
              </Text>
            </Flex>
          ) : null}

          <SimpleGrid columns={[1, 1, 2]} spacing="4">
            {filteredFoods.map((item) => (
              <FoodCard key={item.id} item={item} showVendor={false} />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>

      <Footer />
    </Stack>
  );
};

export default VendorFoods;
